import {Component, OnDestroy, OnInit, AfterViewChecked, ChangeDetectorRef} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {forkJoin, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, take, takeUntil} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {Common} from '../../../../common/util/Util';
import {CustomValidator} from '../../../../common';
import {PayerDTO} from '../../../models/PayerDTO';
import {ProviderNetworkDTO} from '../../../models/ProviderNetworkDTO';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {ProductService} from '../../../services/product.service';
import {SharedService} from '../../../../common/services/shared-service';
import {ProductDTO} from '../../../models/ProductDTO';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {LookupService} from '../../../../common/services/lookup.service';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {PayerService} from '../../../services/payer.service';
import {ProviderNetworkService} from '../../../services/provider-network.service';
import {EntRequestBuilder} from '../../../services/ent-request-builder';
import {BenefitService} from '../../../services/benefit.service';
import {LimitService} from '../../../services/limit.service';
import {ProductBenefitLimitLinkDTO} from '../../../models/ProductBenefitLimitLinkDTO';
import {PBLTreeService} from './tree-benefits-and-limits/tree-benefits-and-limits.component';
import {DateUtil} from '../../../../common/util/DateUtil';
import moment from 'moment';

@Component({
  selector: 'app-add-product',
  templateUrl: './add-product-view.component.html',
  styleUrls: [
    '../../../../../../styles/global/_add-view-edit-styles.scss',
    'add-product-view.component.scss'
  ]
})
export class AddProductViewComponent implements OnInit, OnDestroy, AfterViewChecked {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly lookupValue = LookupValue;
  readonly payerTextChanged$ = new Subject<string>();
  readonly providerNetworkTextChanged$ = new Subject<string>();

  heading: string;
  backPageName: string;
  isEditMode: boolean;
  productId: number;

  productFormGroup: FormGroup;
  productDTO: ProductDTO;

  reportingClassLookUpValues: NameValueDTO[];
  regulatorLookUpValues: NameValueDTO[];
  processingClassLookUpValues: NameValueDTO[];
  currencyLookUpValues: NameValueDTO[];
  areaOfCoverLookUpValues: NameValueDTO[];

  selectedPayer: PayerDTO;
  selectedProviderNetwork: ProviderNetworkDTO;
  filteredPayers: PayerDTO[];
  filteredProviderNetworks: ProviderNetworkDTO[];

  productBenefitLimitLinkDTOList: ProductBenefitLimitLinkDTO[]; // store benefits and list from child component
  isPBLFormValid: boolean;

  private loggedInUserName: string;

  constructor(private changeDetect: ChangeDetectorRef,
              public dialog: MatDialog,
              private benefitService: BenefitService,
              private formBuilder: FormBuilder,
              private limitService: LimitService,
              private lookupService: LookupService,
              private pblTreeService: PBLTreeService,
              private productService: ProductService,
              private payerService: PayerService,
              private providerNetworkService: ProviderNetworkService,
              private route: ActivatedRoute,
              private router: Router,
              private sharedService: SharedService) {

    this.heading = 'Add Product';
    this.backPageName = 'enterprise-management';
    this.productDTO = null;
    this.filteredPayers = [];
    this.filteredProviderNetworks = [];
    this.productBenefitLimitLinkDTOList = [];
    this.loggedInUserName = this.sharedService.getLoggedInUserName();

    if (this.route.snapshot.url[1].path === 'edit-product') {
      this.heading = 'Edit Product';
      this.isEditMode = true;
    } else if (this.route.snapshot.url[1].path === 'add-product') {
      this.isEditMode = false;
    }
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.initLookUpValues();
    this.initProductFormControls();
    this.getHelpInfo('Product');
    if (this.route.snapshot.url[1].path === 'edit-product') {
      this.getProductByProductId(this.productId);
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewChecked(): void {
    this.changeDetect.detectChanges();
  }

  initSubscriptions() {
    // Route Subscription
    this.route.params.subscribe(params => {
      this.productId = params.id;
    });

    // ----------------------------------------
    this.payerTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        debounceTime(this.debounceTime)
      )
      .subscribe((searchValue: string) => {
          this.getFilteredListOfPayer(searchValue);
        },
        error => console.log(error));

    this.providerNetworkTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        debounceTime(this.debounceTime)
      )
      .subscribe((searchValue: string) => {
          this.getFilteredListOfProviderNetworkPayer(searchValue);
        },
        error => console.log(error));

  }

  initLookUpValues() {
    forkJoin(
      this.lookupService.getListOfLookupValues(this.lookupValue.Regulator),
      this.lookupService.getListOfLookupValues(this.lookupValue.ReportingClass),
      this.lookupService.getListOfLookupValues(this.lookupValue.ProcessingClass),
      this.lookupService.getListOfLookupValues(this.lookupValue.Currency),
      this.lookupService.getListOfLookupValues(this.lookupValue.AreaOfCover)
    )
      .pipe(
        take(1)
      )
      .subscribe(
        (responses: any) => {
          if (responses[0]) {
            this.regulatorLookUpValues = responses[0];
          }
          if (responses[1]) {
            this.reportingClassLookUpValues = responses[1];
          }
          if (responses[2]) {
            this.processingClassLookUpValues = responses[2];

            const defaultValue: NameValueDTO = this.processingClassLookUpValues.find((item: NameValueDTO) => item.name === 'Medical');
            if (defaultValue) {
              this.productFormGroup.patchValue({
                processingClass: defaultValue.value,
              });
            }
          }
          if (responses[3]) {
            this.currencyLookUpValues = responses[3];

            const defaultValue: NameValueDTO = this.currencyLookUpValues.find((item: NameValueDTO) => item.name === 'Rand');
            if (defaultValue) {
              this.productFormGroup.patchValue({
                currency: defaultValue.value,
              });
            }
          }
          if (responses[4]) {
            this.areaOfCoverLookUpValues = responses[4];

            const defaultValue: NameValueDTO = this.areaOfCoverLookUpValues.find((item: NameValueDTO) => item.name === 'South Africa');
            if (defaultValue) {
              this.productFormGroup.patchValue({
                areaOfCover: defaultValue.value,
              });

            }
          }

          // Set values: Once ALL local instance variables are set
          // Note: In Add Case default value for "Processing Class" = Medical
          // base on Processing class Reporting class value is set.

          if (!this.isEditMode) {
            this.handleDefaultValueForReportingClassBasedOn('Medical');
          }
        },
        (error) => {
          console.log(error);
        });
  }

  initProductFormControls() {
    // Get "First" day of the NEXT(Following) year:
    const currentYear = new Date().getFullYear();
    const startDate = moment(this.sharedService.formatDate(new Date(currentYear + 1, 0, 1), DateUtil.dateFormatWithoutSlashes))

    // Static Value defined
    const endDate = moment(this.sharedService.formatDate(new Date('2099-12-31'), DateUtil.dateFormatWithoutSlashes))

    this.productFormGroup = this.formBuilder.group({
      productId: new FormControl(null),
      name: new FormControl(null, [Validators.required]),
      startDate: new FormControl(startDate, [Validators.required]),
      endDate: new FormControl(endDate, [Validators.required]),
      approvalNumber: new FormControl(null, [Validators.required]),
      claimingDays: new FormControl(null, [Validators.required, CustomValidator.wholeNumber]),
      bookingDays: new FormControl(null, [Validators.required, CustomValidator.wholeNumber]),
      productCode: new FormControl(null, [Validators.required]),
      emailNotification: new FormControl(null, [CustomValidator.email]),
      payer: new FormControl(null, [Validators.required]),
      reportingClass: new FormControl(null, [Validators.required]),
      regulator: new FormControl(null, [Validators.required]),
      processingClass: new FormControl(null, [Validators.required]),
      providerNetwork: new FormControl(null, [Validators.required]),
      currency: new FormControl(null, [Validators.required]),
      areaOfCover: new FormControl(null, [Validators.required]),
      productBenefitLimitLinkDTO: []
    });
  }

  getHelpInfo(componentName: string) {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onPayerSelected(name: string) {
    if (this.isPayerExist()) {
      this.selectedPayer = this.filteredPayers.find((item: PayerDTO) => item.name === name);
    }
  }

  onProviderNetworkSelected(name: string) {
    if (this.isProviderNetworkExist()) {
      this.selectedProviderNetwork = this.filteredProviderNetworks
        .find((item: ProviderNetworkDTO) => item.name === name);
    }
  }

  onInputChangedUpdateProductName($event: any) {
    if (this.isEditMode) {
      this.heading = `Edit Product - ${$event.target.value}`;
    } else {
      this.heading = `Add Product - ${$event.target.value}`;
    }
  }

  clearPayer() {
    this.productFormGroup.get('payer').reset();
    this.selectedPayer = null;
  }

  clearProviderNetwork() {
    this.productFormGroup.get('providerNetwork').reset();
    this.selectedProviderNetwork = null;
  }

  onBlurPayerSelected() {
    if (this.isPayerExist()) {
      const value = this.productFormGroup.get('payer').value;
      const isMatched = this.filteredPayers.find((item: PayerDTO) => {
        return item.name === value;
      });
      if (!isMatched) {
        this.clearPayer();
      }
    } else {
      this.clearPayer();
    }
  }

  onBlurProviderNetworkSelected() {
    if (this.isProviderNetworkExist()) {
      const value = this.productFormGroup.get('providerNetwork').value;
      const isMatched = this.filteredProviderNetworks.find((item: ProviderNetworkDTO) => {
        return item.name === value;
      });
      if (!isMatched) {
        this.clearProviderNetwork();
      }
    } else {
      this.clearProviderNetwork();
    }
  }

  onBlurTruncateDecimalPart(formControlName: string) {
    this.productFormGroup.patchValue({
      [formControlName]: Math.trunc(this.productFormGroup.get(formControlName).value)
    });
  }

  onProductBenefitLimitLinkDTOListChanged($event: ProductBenefitLimitLinkDTO[]) {

    if (this.isEditMode) {
      this.productDTO.productBenefitLimitLinkDTO = $event;
    } else {
      this.productBenefitLimitLinkDTOList = $event;
    }

    this.saveProduct();
  }

  saveProduct() {
    const productDTOPayload = this.getProductDTOPayload();

    this.productService.saveProduct(productDTOPayload)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe((response: ProductDTO | any) => {
          if (response) {
            if (this.isEditMode) {
              this.sharedService.openSnackBar(`Save product edited successfully`, 'Dismiss');
            } else {
              this.sharedService.openSnackBar(`New product added successfully`, 'Dismiss');
            }
            this.router.navigate(['/enterprise-management'], {queryParams: {tab: 'products'}});
          } else if (response && response.businessMessage) {
            this.sharedService.openSnackBar(`${response.businessMessage}`, 'Dismiss');
          }
        },
        (error) => {
          console.error('Failed to save product:', error);
          this.sharedService.openErrorDialog(error.message);
        });
  }

  resetState() {
    this.resetProductFormGroup();
    this.resetProductDTO();
  }

  onSelectionChanged() {
    this.initializeNewProductTree();
    this.initializeProductDTO();
  }

  private initializeNewProductTree() {
    const treeData = {
      [this.productFormGroup.controls['name'].value]: {}
    };
    this.pblTreeService.initialize(treeData);
  }

  private initializeProductDTO() {
    this.productDTO = this.getProductDTOPayload();
  }

  private resetProductFormGroup() {
    this.productFormGroup.reset();
  }

  private resetProductDTO() {
    this.productDTO = null;
  }

  private isProviderNetworkExist() {
    return !!(this.filteredProviderNetworks && this.filteredProviderNetworks.length > 0);
  }

  private isPayerExist() {
    return !!(this.filteredPayers && this.filteredPayers.length > 0);
  }

  private getProductByProductId(productId: number): void {
    this.productService.getProductByProductId(productId)
      .pipe(
        take(1)
      )
      .subscribe(
        (response: ProductDTO) => {
          if (response) {
            this.productDTO = response;
            this.setFormsForEdit(response);
          } else {
            this.sharedService.openSnackBar(`Product by ${productId} not found`, 'Dismiss');
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private setFormsForEdit(data: ProductDTO) {
    this.setProductFormForEdit(data);
  }

  private setProductFormForEdit(product: ProductDTO) {
    const {
      productId,
      name,
      startDate,
      endDate,
      approvalNumber,
      claimingDays,
      bookingDays,
      productCode,
      emailNotification,
      payer,
      reportingClass,
      regulator,
      processingClass,
      providerNetwork,
      currency,
      areaOfCover,
      productBenefitLimitLinkDTO
    } = product;

    // Set values:
    this.heading = `Edit Product - ${name}`;
    this.productFormGroup.patchValue({
      productId,
      name,
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      approvalNumber,
      claimingDays,
      bookingDays,
      productCode,
      emailNotification,
      payer: payer.name,
      reportingClass: reportingClass.value,
      regulator: regulator.value,
      processingClass: processingClass.value,
      providerNetwork: providerNetwork.name,
      currency: currency.value,
      areaOfCover: areaOfCover.value,
      productBenefitLimitLinkDTO
    });


    this.getFilteredListOfPayer(payer.name);
    this.getFilteredListOfProviderNetworkPayer(providerNetwork.name);
    this.handleDefaultValueForReportingClassBasedOn(processingClass.name);
  }

  private handleDefaultValueForReportingClassBasedOn(processingClass: string) {
    if (processingClass === 'Medical') {
      const defaultValue: NameValueDTO = this.reportingClassLookUpValues.find((item: NameValueDTO) => {
        return item.name === 'Risk - Individual - Individual';
      });
      if (defaultValue) {
        this.productFormGroup.patchValue({
          reportingClass: defaultValue.value,
        });
      }

      const defaultRegulatorNameValue: NameValueDTO = this.regulatorLookUpValues.find((item: NameValueDTO) => {
        return item.name === 'Health Professions Council of South Africa';
      });
      if (defaultRegulatorNameValue) {
        this.productFormGroup.patchValue({
          regulator: defaultRegulatorNameValue.value,
        });
      }

    } else if (processingClass === 'Funeral') {
      const defaultValue: NameValueDTO = this.reportingClassLookUpValues.find((item: NameValueDTO) => {
        return item.name === 'Risk - Funeral – Individual - Funeral – Individual';
      });
      if (defaultValue) {
        this.productFormGroup.patchValue({
          reportingClass: defaultValue.value,
        });
      }
    }
  }

  private getFilteredListOfPayer(payerName: string): void {
    let pagedRequestDTO: PagedRequestDTO;
    pagedRequestDTO = EntRequestBuilder.getFilteredListOfPayersPagedRequestDTO(payerName);

    this.payerService
      .getListOfPayers(pagedRequestDTO)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: PagedResponseDTO<PayerDTO>) => {
          this.filteredPayers = response.resultList;
          if (payerName) {
            this.selectedPayer = this.filteredPayers.find(payer => payer.name === payerName);
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getFilteredListOfProviderNetworkPayer(providerNetworkName: string): void {
    let pagedRequestDTO: PagedRequestDTO;
    pagedRequestDTO = EntRequestBuilder.getFilteredListOfProviderNetworkPagedRequestDTO(providerNetworkName);

    this.providerNetworkService
      .getListOfProviderNetwork(pagedRequestDTO)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: PagedResponseDTO<ProviderNetworkDTO>) => {
          this.filteredProviderNetworks = response.resultList;

          if (providerNetworkName) {
            this.selectedProviderNetwork = this.filteredProviderNetworks.find(network => network.name === providerNetworkName);
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getProductDTOPayload() {
    let payload: ProductDTO | any;

    const reportingClass = this.productFormGroup.get('reportingClass').value;
    const regulator = this.productFormGroup.get('regulator').value;
    const processingClass = this.productFormGroup.get('processingClass').value;
    const currency = this.productFormGroup.get('currency').value;
    const areaOfCover = this.productFormGroup.get('areaOfCover').value;

    payload = {
      productId: this.isEditMode ? this.productDTO.productId : null,
      name: this.productFormGroup.get('name').value,
      startDate: new Date(this.productFormGroup.get('startDate').value),
      endDate: new Date(this.productFormGroup.get('endDate').value),
      approvalNumber: this.productFormGroup.get('approvalNumber').value,
      claimingDays: this.productFormGroup.get('claimingDays').value,
      bookingDays: this.productFormGroup.get('bookingDays').value,
      productCode: this.productFormGroup.get('productCode').value,
      emailNotification: this.productFormGroup.get('emailNotification').value,

      // Note: Payer/Provider Network mat-autocomplete [value]=<WITH NAME> binding
      payer: {value: this.selectedPayer.payerId, name: this.selectedPayer.name},
      providerNetwork: {value: this.selectedProviderNetwork.providerNetworkId, name: this.selectedProviderNetwork.name},
      // Note: mat-select [value] -> binding:
      reportingClass: {
        value: reportingClass,
        name: this.reportingClassLookUpValues.find(type => type.value === reportingClass).name
      },
      regulator: {value: regulator, name: this.regulatorLookUpValues.find(type => type.value === regulator).name},
      processingClass: {
        value: processingClass,
        name: this.processingClassLookUpValues.find(type => type.value === processingClass).name
      },
      currency: {value: currency, name: this.currencyLookUpValues.find(type => type.value === currency).name},
      areaOfCover: {
        value: areaOfCover,
        name: this.areaOfCoverLookUpValues.find(type => type.value === areaOfCover).name
      },
      productBenefitLimitLinkDTO: this.isEditMode ? this.productDTO.productBenefitLimitLinkDTO : this.productBenefitLimitLinkDTOList,
      created: this.isEditMode ? this.productDTO.created : new Date(),
      createdBy: this.isEditMode ? this.productDTO.createdBy : this.loggedInUserName,
      tenantId: this.isEditMode ? this.productDTO.tenantId : null,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null
    };
    
    return payload;
  }
}
